import { useState, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { VclDocumentDto } from '@ctms/contracts';
import { visitLabel } from '@ctms/shared';
import { Eye, FileText, Loader2, Printer, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { documentsApi } from '../documents.api.js';

export interface VclPrintTarget {
  visitId: number;
  visitLabel: string;
  patientName: string;
  patientCode: string;
}

export interface VclPrintDialogProps {
  target: VclPrintTarget;
  onClose: () => void;
}

/**
 * Triggers the browser download of the rendered A4 PDF (REQ-DOC-007). Kept as
 * a named export so the anchor mechanics are unit-testable without a dialog.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Read-only preview of the composed document (REQ-DOC-009…015 data). */
function VclPreview({ doc }: { doc: VclDocumentDto }): JSX.Element {
  const h = doc.header;
  const w = h.visitWindow;
  return (
    <div className="space-y-4" data-testid="vcl-preview">
      <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Nagłówek A4
        </h3>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Pacjent:</dt>
            <dd className="font-medium text-slate-200">
              {h.lastName} {h.firstName}
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Nr screening:</dt>
            <dd className="font-mono text-cyan-300">{h.screeningNumber}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Nr randomizacyjny:</dt>
            <dd className="font-mono text-cyan-300">{h.randomizationNumber ?? '—'}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">PESEL:</dt>
            <dd className="font-mono text-slate-300">{h.maskedPesel ?? '—'}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Telefon:</dt>
            <dd className="font-mono text-slate-300">{h.maskedPhones[0] ?? '—'}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Ur.:</dt>
            <dd className="text-slate-300">
              {h.birthDate ?? '—'}
              {h.ageAtVisit !== null ? ` (${h.ageAtVisit} lat)` : ''}
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Wizyta:</dt>
            <dd className="text-slate-200">
              V{h.visitNumber} {visitLabel(h.visitName1, h.visitName2)}
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Termin:</dt>
            <dd className="text-slate-300">
              {h.visitDate ?? '—'}
              {h.dayOfWeek !== null ? ` (${h.dayOfWeek})` : ''}
              {h.visitTime !== null ? ` ${h.visitTime}` : ''}
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Okno wizyty:</dt>
            <dd className="text-slate-300">
              {w.enabled && w.referralVisitNumber !== null
                ? `${w.minDays}/${w.optDays}/${w.maxDays} dni od V${w.referralVisitNumber}`
                : '—'}
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="text-slate-500">Protokół / ośrodek:</dt>
            <dd className="font-mono text-slate-300">
              {h.protocolNumber} · {h.siteNumber}
            </dd>
          </div>
        </dl>
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Check-lista wizyty
        </h3>
        {doc.sections.length === 0 ? (
          <p className="text-xs text-slate-500">Brak procedur w układzie tej wizyty.</p>
        ) : (
          <div className="space-y-3">
            {doc.sections.map((section) => (
              <div key={section.mainGroupId}>
                <h4 className="text-xs font-bold uppercase tracking-wide text-slate-200">
                  {section.name}{' '}
                  <span className="ml-1 rounded border border-slate-700 px-1.5 py-0.5 text-[10px] font-medium normal-case text-cyan-300">
                    {section.role}
                  </span>
                </h4>
                <ul className="mt-1 space-y-0.5">
                  {section.items.map((item) => (
                    <li key={item.subGroupId} className="text-xs text-slate-300">
                      {item.name} — <span className="text-slate-400">{item.role}</span>
                      {item.roleInherited ? (
                        <span className="ml-1 text-[10px] text-slate-500">(z grupy)</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Open tasks
        </h3>
        {doc.openTasks.length === 0 ? (
          <p className="text-xs text-slate-500">Brak otwartych zadań.</p>
        ) : (
          <ul className="space-y-0.5">
            {doc.openTasks.map((task) => (
              <li key={task.taskId} className="text-xs text-slate-300">
                {task.name} <span className="text-slate-500">(od {task.startDate})</span>
                {task.comments !== null ? ` — ${task.comments}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * Visit check-list (VCL) dialog — the Cockpit trigger of Domain 7
 * (Q-DOC-007 resolved: the Cockpit visit row). ONE visit identifier drives
 * both endpoints: `generate` composes the JSON preview (REQ-DOC-001…016),
 * `renderPdf` returns the physical A4 printout (REQ-DOC-007/008, SLA < 1,5 s).
 * The paper notes area (REQ-DOC-016) exists only on the printout.
 */
export function VclPrintDialog({ target, onClose }: VclPrintDialogProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pdfNotice, setPdfNotice] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: (): Promise<VclDocumentDto> => documentsApi.generateVcl(target.visitId),
    onError: (error) => {
      void getApiErrorMessage(error).then(setErrorMessage);
    },
  });

  const renderPdf = useMutation({
    mutationFn: (): Promise<Blob> => documentsApi.renderVclPdf(target.visitId),
    onSuccess: (blob) => {
      downloadBlob(blob, `vcl-${target.visitId}.pdf`);
      setPdfNotice(`Pobrano check-listę VCL wizyty ${target.visitLabel}`);
    },
    onError: (error) => {
      void getApiErrorMessage(error).then(setErrorMessage);
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Check-lista wizyty (VCL)"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs"
    >
      <div className="flex max-h-[85vh] w-full max-w-[900px] flex-col rounded-xl border border-slate-800 bg-slate-900 text-slate-100 shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-950 text-cyan-400 ring-1 ring-cyan-800">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-100">Check-lista wizyty (VCL)</h2>
              <div className="mt-1 flex items-center gap-4 text-xs text-slate-400">
                <span>
                  Pacjent: <strong className="text-slate-200">{target.patientName}</strong> ·{' '}
                  <span className="font-mono text-cyan-300">{target.patientCode}</span>
                </span>
                <span>•</span>
                <span>
                  Wizyta: <strong className="text-slate-200">{target.visitLabel}</strong>
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="close-vcl-dialog"
            onClick={onClose}
            aria-label="Zamknij okno check-listy VCL"
            className="rounded-md border border-slate-700 bg-slate-850 p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          {errorMessage !== null && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-rose-700 bg-rose-950/80 px-4 py-2 text-xs text-rose-200"
            >
              {errorMessage}
            </div>
          )}
          {generate.data !== undefined ? (
            <VclPreview doc={generate.data} />
          ) : (
            <p className="text-xs text-slate-500">
              Podgląd nie został jeszcze wygenerowany — użyj przycisku „Podgląd” albo od razu
              pobierz wydruk A4 (PDF).
            </p>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-800 px-6 py-4">
          <div aria-live="polite" className="text-xs text-emerald-300">
            {pdfNotice}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setErrorMessage(null);
                generate.mutate();
              }}
              disabled={generate.isPending}
              className="flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-800 disabled:opacity-50"
            >
              {generate.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
              Podgląd
            </button>
            <button
              type="button"
              onClick={() => {
                setErrorMessage(null);
                setPdfNotice(null);
                renderPdf.mutate();
              }}
              disabled={renderPdf.isPending}
              className="flex items-center gap-1.5 rounded-md border border-cyan-700 bg-cyan-950/60 px-3 py-1.5 text-xs font-medium text-cyan-200 transition-colors hover:bg-cyan-900/60 disabled:opacity-50"
            >
              {renderPdf.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Printer className="h-3.5 w-3.5" />
              )}
              Pobierz PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:bg-slate-800"
            >
              Zamknij
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
